<script setup lang="ts">
const props = defineProps<{
	label: string
	tooltip?: string | null
}>()

const badgeRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const tooltipStyle = ref({ top: '0px', left: '0px' })
const arrowOffset = ref('50%')

function updatePosition(): void {
	if (!badgeRef.value || !tooltipRef.value) {
		return
	}

	const badgeRect = badgeRef.value.getBoundingClientRect()
	const tooltipRect = tooltipRef.value.getBoundingClientRect()
	const padding = 8

	const badgeCenterX = badgeRect.left + badgeRect.width / 2
	let left = badgeCenterX - tooltipRect.width / 2

	if (left < padding) {
		left = padding
	} else if (left + tooltipRect.width > window.innerWidth - padding) {
		left = window.innerWidth - padding - tooltipRect.width
	}

	const arrowLeft = badgeCenterX - left
	arrowOffset.value = `${arrowLeft}px`

	tooltipStyle.value = {
		top: `${badgeRect.top + window.scrollY}px`,
		left: `${left}px`,
	}
}

function show(): void {
	if (!props.tooltip) {
		return
	}

	visible.value = true
	nextTick(updatePosition)
}

function hide(): void {
	visible.value = false
}

function toggle(): void {
	if (visible.value) {
		hide()
	} else {
		show()
	}
}

onMounted(() => {
	window.addEventListener('scroll', hide, { passive: true })
})

onUnmounted(() => {
	window.removeEventListener('scroll', hide)
})
</script>

<template>
	<span
		ref="badgeRef"
		class="inline-flex cursor-help items-center gap-1 rounded-full bg-primary p-1 text-xs text-white"
		@mouseenter="show"
		@mouseleave="hide"
		@click.stop="toggle"
	>
		<i-gg-danger class="size-4" />
		{{ label }}
	</span>

	<Teleport to="body">
		<div
			v-if="visible && tooltip"
			ref="tooltipRef"
			class="pointer-events-none fixed z-[9999] -translate-y-full"
			:style="tooltipStyle"
		>
			<div class="relative mb-2 max-w-[min(48rem_,_90vw)] text-wrap rounded bg-primary px-3 py-2 text-xs text-white shadow-lg">
				{{ tooltip }}
				<span
					class="absolute top-full border-4 border-transparent border-t-primary"
					:style="{ left: arrowOffset, transform: 'translateX(-50%)' }"
				/>
			</div>
		</div>
	</Teleport>
</template>
